import { useI18n, useTranslate } from "@/lib/i18n";
import type { Locale } from "@/lib/i18n/config";
import type { TranslateFn } from "@/lib/i18n/intl";
import type { PaperWidth, PrintHardwareActions } from "@/lib/printing/types";
import { billPlaceLabel, channelLabel } from "@/lib/invoice/place";
import { kitchenFirstName } from "@/lib/invoice/paperLines";
import { chitAdditionNumber, chitClock, groupChitLines } from "@/lib/invoice/chitLines";
import type { KitchenChitLine, KitchenChitView } from "@/lib/api/types";
import { voidReasonLabelForCode } from "@/modules/pos/lib/voidReasons";
import {
  renderStandalonePrintArtifact,
  type PrintArtifactAction,
  type StandalonePrintArtifact,
} from "@/modules/printing/renderDocument";

/**
 * The KOT — the chit that comes out of the kitchen printer.
 *
 * Deliberately built the same way as `pos/components/Receipt.tsx`, and on the
 * SAME `.receipt` print scope: rendered into the page and revealed only by the
 * print stylesheet, so `window.print()` needs no popup, no round trip and no
 * second data fetch. The two never coexist on a screen — the till prints bills,
 * the kitchen prints chits — so sharing the scope costs nothing and means one
 * `@page` block governs both papers instead of two that can drift.
 *
 * ─── THE HARD RULE ───────────────────────────────────────────────────────────
 * A CHIT CARRIES NO PRICES. Not a line price, not an addon price, not a total.
 * The kitchen has no bill to settle, and a piece of paper that shows money is a
 * piece of paper that will eventually be handed to a customer AS the bill —
 * one that has not been discounted, comped or taxed. `KitchenChitView` does not
 * even carry the numbers (§7 of the kitchen design, and `loadTicketForPrint`
 * never selects them), so this is enforced at the wire and not only here.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Everything on it answers one of four questions a cook asks: what am I making,
 * for which table, in what order, and is this paper new.
 */
export function KitchenChit({
  chit,
  action,
}: {
  chit: KitchenChitView;
  /**
   * D172 — what this paper IS, when the caller knows: `ORIGINAL` for a ticket
   * that never reached the kitchen («لم تصل التذكرة»), `REPRINT` for an extra
   * copy («نسخة إضافية»). Absent, the stored print count decides, as before.
   */
  action?: PrintArtifactAction;
}) {
  const t = useTranslate();
  const { locale, direction } = useI18n();
  const model = buildKitchenChitRenderModel({
    chit,
    locale,
    direction,
    t,
    action,
  });
  return <KitchenChitTemplate {...model} />;
}

export type KitchenChitTemplateProps = {
  chit: KitchenChitView;
  locale: Locale;
  direction: "rtl" | "ltr";
  t: TranslateFn;
  paperWidth?: PaperWidth;
  action?: PrintArtifactAction;
};

export type KitchenChitRenderModel = KitchenChitTemplateProps & {
  paperWidth: PaperWidth;
  action: PrintArtifactAction;
};

export function buildKitchenChitRenderModel(
  input: KitchenChitTemplateProps,
): KitchenChitRenderModel {
  return {
    ...input,
    paperWidth: input.paperWidth ?? "80mm",
    action:
      input.action ?? (input.chit.printCount > 0 ? "REPRINT" : "ORIGINAL"),
  };
}

/** Pure template shared by the mounted browser fallback and provider HTML. */
export function KitchenChitTemplate({
  chit,
  locale,
  direction,
  t,
  paperWidth = "80mm",
  action,
}: KitchenChitTemplateProps) {
  // `printCount` is the count BEFORE this print — the server increments it when
  // the command lands — so anything above zero means paper already came out for
  // this exact ticket.
  const artifactAction =
    action ?? (chit.printCount > 0 ? "REPRINT" : "ORIGINAL");
  const isReprint = artifactAction === "REPRINT";
  const addition = chitAdditionNumber(chit.round);
  const customer = kitchenFirstName(chit.customerName);
  const stationName =
    locale === "en" && chit.stationNameEn
      ? chit.stationNameEn
      : chit.stationNameAr;

  return (
    <div
      className="receipt kitchen-chit"
      dir={direction}
      lang={locale}
      aria-hidden="true"
      data-print-artifact="KITCHEN_CHIT"
      data-print-action={artifactAction}
      data-paper-width={paperWidth}
    >
      <div className="print-artifact-kind">
        {t((m) => m.printArtifact.kitchenChit)}
      </div>
      {artifactAction === "CHANGE" || artifactAction === "VOID" ? (
        <div
          className="print-artifact-marker"
          style={{
            fontSize: "17px",
            fontWeight: 900,
            // T14 — the cancellation reversed, so it cannot pass for a heading.
            // `print-color-adjust: exact`: Chrome drops backgrounds when
            // printing by default, which would print white on white.
            ...(artifactAction === "VOID"
              ? {
                  background: "#000",
                  color: "#fff",
                  printColorAdjust: "exact",
                  WebkitPrintColorAdjust: "exact",
                }
              : {}),
          }}
        >
          {t((m) =>
            artifactAction === "VOID"
              ? m.printArtifact.void
              : m.printArtifact.change,
          )}
        </div>
      ) : null}
      {/*
        The REPRINT banner is first, biggest and boxed, because of what it
        prevents: a second chit for round 2 read as round 3 means the line cooks
        a duplicate of food that is already on the pass. A cook reads the top
        edge of a chit as it comes off the printer and often nothing else, so
        the warning has to be there rather than in a footer.
      */}
      {isReprint ? (
        <div
          className="print-artifact-marker"
          style={{
            border: "2px solid #000",
            padding: "1.5mm",
            marginBottom: "2mm",
            textAlign: "center",
            fontWeight: 900,
            fontSize: "17px",
            letterSpacing: "0.04em",
          }}
        >
          {/* T14 — «نسخة مكررة», only on an extra copy: a paper re-sent
              because the first never arrived prints as an ORIGINAL. */}
          {t((m) => m.printArtifact.reprint)}
          <div style={{ fontSize: "10px", fontWeight: 600 }}>
            {t((m) => m.printArtifact.reprintNote)}
          </div>
          {/* WHICH copy this is, not just "a copy": with two reprints on the
              rail, "نسخة رقم 3" is what lets a cook bin the older one. The
              stored count is pre-increment, so this paper is count + 1. */}
          <div
            style={{ fontSize: "11px", fontWeight: 800, marginTop: "0.5mm" }}
          >
            {t((m) => m.kitchen.chitPrintNumber, {
              number: chit.printCount + 1,
            })}
          </div>
        </div>
      ) : null}

      {/* Station first and largest: a printer serves one section, but a chit
          that walked to the wrong pass has to say where it belongs. */}
      <div style={{ textAlign: "center", fontWeight: 800, fontSize: "16px" }}>
        {stationName}
      </div>

      {/* Table and round, the two things called across a kitchen out loud. The
          round number is per INVOICE, so "table 7, round 2" is unambiguous even
          when the grill and the bar each hold a chit numbered 2. */}
      <div
        style={{
          textAlign: "center",
          fontWeight: 800,
          fontSize: "15px",
          marginTop: "1mm",
        }}
      >
        {/* T14 — «إضافة n» from round 2 (n = round − 1); nothing on the
            first fire, which is not an addition to anything. */}
        {[
          billPlaceLabel(
            { tableIds: chit.tableNames, channel: chit.channel },
            (name) => name,
            t,
          ),
          addition !== null
            ? t((m) => m.printArtifact.addition, { number: addition })
            : null,
        ]
          .filter((value): value is string => Boolean(value))
          .join(" · ")}
      </div>

      {/* T9/T19 — WHOSE bag, on an off-premise ticket: the BAND — the daily
          number large, then channel · platform · platform order no. — and the
          customer's FIRST NAME, so two "Ahmed" orders on the pass do not swap
          and a cook can call it out. Never the phone or the address: a chit is
          left lying on the pass. A dine-in plate keeps its small bill line. */}
      {chit.channel !== "DINE_IN" ? (
        <div
          style={{ textAlign: "center", marginTop: "1.5mm" }}
          data-testid="kitchen-chit-band"
        >
          <div style={{ fontSize: "24px", fontWeight: 900, lineHeight: 1.1 }}>
            #{chit.dailySequence}
          </div>
          {/* Each segment in its own `<bdi>`: a Latin platform name or a
              Latin order reference inside an Arabic run otherwise reorders
              across the separators and «QA» fuses onto the ref (cook persona,
              2026-10-07). Isolation keeps "channel · platform · ref" reading
              in that order in both locales. */}
          <div style={{ fontWeight: 800, fontSize: "13px" }}>
            {[
              channelLabel(chit.channel, t),
              chit.aggregatorName ?? null,
              chit.platformOrderRef ?? null,
            ]
              .filter((value): value is string => Boolean(value))
              .map((segment, index) => (
                <span key={segment}>
                  {index > 0 ? " · " : null}
                  <bdi>{segment}</bdi>
                </span>
              ))}
          </div>
          {/* The staff meal's bill name prints whole (D172). */}
          {customer ? (
            <div
              style={{ fontWeight: 800, fontSize: "16px" }}
              data-testid="kitchen-chit-customer"
            >
              {customer}
            </div>
          ) : null}
        </div>
      ) : (
        <div style={{ textAlign: "center", marginTop: "1mm" }}>
          {t((m) => m.kitchen.chitInvoice, { sequence: chit.dailySequence })}
        </div>
      )}
      <div style={{ textAlign: "center" }}>
        {/* Fire time, not print time: how long the food has been owed is the
            number the cook is judging, and on a reprint the two differ. */}
        {`${chitClock(chit.firedAt, chit.timezone)}${chit.firedBy ? ` · ${chit.firedBy}` : ""}`}
      </div>

      <hr style={{ margin: "2mm 0", borderTop: "1px solid #000" }} />

      {/* Item lines (T10, D172). With the store's grouping on (the default),
          identical plates of this ticket print as ONE line with the count
          FIRST at twice the dish's size — "3×" — and a remade plate stays on
          its own line. Off, one row per plate as before (D15). */}
      <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {groupChitLines(chit.items, chit.grouped ?? true, (item) =>
          chitLineFacts(item, locale),
        ).map(({ line: item, count, key }) => (
          <li key={key} style={{ marginBottom: "1.5mm" }} data-chit-line="">
            <div
              style={{ display: "flex", alignItems: "baseline", gap: "2mm" }}
            >
              {count > 1 ? (
                <bdi
                  dir="ltr"
                  data-chit-count=""
                  style={{ fontSize: "36px", fontWeight: 900, lineHeight: 1 }}
                >
                  {`${count}×`}
                </bdi>
              ) : null}
              <span style={{ fontWeight: 800, fontSize: "18px" }}>
                {chitDishName(item, locale)}
              </span>
            </div>
            {/* T10 — side and doneness on their own line under the name. */}
            {chitVariants(item, locale).length > 0 ? (
              <div
                style={{ fontWeight: 800, fontSize: "14px" }}
                data-chit-variant=""
              >
                {chitVariants(item, locale).join(" · ")}
              </div>
            ) : null}
            {/* What a combo plate actually contains (S16, D52). Printed as
                SUB-LINES under the dish, above the addons, because they are
                the food itself rather than a modifier of it: without them the
                paper says "Meal Deal" and nothing about what to cook.
                No prices — a component has none, and the chit has none. */}
            {(item.comboComponents ?? []).length > 0 ? (
              <div style={{ fontSize: "12px", paddingInlineStart: "3mm" }}>
                {(item.comboComponents ?? [])
                  .map((component) => {
                    const label =
                      locale === "en" && component.nameEn
                        ? component.nameEn
                        : component.nameAr;
                    // B117/A6 — «×» (U+00D7), the glyph D65 writes and the till
                    // and the board both use. An ASCII `x` here made one
                    // repeat count read two ways across three surfaces of the
                    // same plate.
                    return component.quantity > 1
                      ? `${label} ×${component.quantity}`
                      : label;
                  })
                  .join("  ")}
              </div>
            ) : null}
            {(item.substitutions ?? []).length > 0 ? (
              // ABOVE the addons and heavier than them, because a swap
              // overrides the recipe rather than adding to it. Both halves are
              // printed: "+ V7" alone reads as an addition and the cook makes
              // the drink with the 7up still in it, while the ledger has
              // already deducted the V7 and the bill has already charged for
              // it. In print mode nobody is at a screen to catch that (D19).
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 800,
                  paddingInlineStart: "3mm",
                }}
              >
                {(item.substitutions ?? [])
                  .map(
                    (swap) =>
                      `− ${swap.removedNameAr}  ⟵  + ${swap.addedNameAr}`,
                  )
                  .join("  ")}
              </div>
            ) : null}
            {/* B118 — FIRST on the plate, as it is on the KDS card. The paper
                printed it LAST, under the note and the add-ons, so the one
                surface a station without a screen works from buried the fact
                that this plate is a remake beneath the ordinary modifiers. The
                two readers of one plate must meet it in the same order. */}
            {item.remakeCount > 0 ? (
              <div style={{ fontSize: "12px", fontWeight: 800 }}>
                {/* T14 — the device chit's catalogue word and «#n», so both
                    renderers print the same thing for one remade plate. */}
                <span
                  data-chit-remake=""
                  style={{ fontSize: "15px", fontWeight: 900 }}
                >
                  {t((m) => m.printArtifact.remake)}
                  <bdi dir="ltr">{` #${item.remakeCount}`}</bdi>
                </span>
                {/* B117/Q4 — the chef's condition for wanting the field at
                    all: a cook who reads COOKED_WRONG knows what to fix
                    before he starts, instead of remaking the same mistake.
                    Same line as the badge so it cannot be read as a separate
                    plate. Null on a remake taken before the field existed. */}
                {item.remakeReason
                  ? ` — ${t((m) => m.kitchen.remakeReasonLine, {
                      reason: voidReasonLabelForCode(
                        t,
                        item.remakeReason,
                        "remake",
                      ),
                    })}`
                  : null}
                {/* B118 — `OTHER` is the one code that says nothing, which is
                    why a note is mandatory beside it. The code reached the cook
                    and the note did not, so the chit read "reason: another
                    reason" with the explanation stranded in the row. */}
                {item.remakeReasonNote ? ` — ${item.remakeReasonNote}` : null}
              </div>
            ) : null}
            {item.note ? (
              // The waiter's free-text instruction, ABOVE the addons and
              // heavier than them — §4 ranks it there and the swap block
              // directly above says so in its own comment, while this printed
              // BELOW them (B117/A3). A modifier changes the recipe and a note
              // overrides it, so a cook scanning the line meets the override
              // first.
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 800,
                  paddingInlineStart: "3mm",
                }}
              >
                {`★ ${item.note}`}
              </div>
            ) : null}
            {item.addons.length > 0 ? (
              // Indented and prefixed, never merged into the name: "بدون بصل"
              // is the difference between a plate that goes out and one that
              // comes back, and it must not read as part of the dish name.
              <div style={{ fontSize: "12px", paddingInlineStart: "3mm" }}>
                {item.addons.map((addon) => `+ ${addon}`).join("  ")}
              </div>
            ) : null}
          </li>
        ))}
      </ul>

      <hr style={{ margin: "2mm 0", borderTop: "1px solid #000" }} />

      <div style={{ textAlign: "center", fontSize: "10px" }}>
        {t((m) => m.kitchen.chitNoPrices)}
      </div>
    </div>
  );
}

export async function renderKitchenChitDocument(
  props: KitchenChitTemplateProps & {
    paperWidth: PaperWidth;
    hardwareActions?: PrintHardwareActions;
  },
): Promise<StandalonePrintArtifact> {
  const model = buildKitchenChitRenderModel(props);
  return await renderStandalonePrintArtifact({
    kind: "KITCHEN_CHIT",
    action: model.action,
    locale: model.locale,
    paperWidth: model.paperWidth,
    title: `KITCHEN_CHIT #${model.chit.dailySequence}`,
    content: <KitchenChitTemplate {...model} />,
    hardwareActions: props.hardwareActions,
  });
}

/** The dish alone when the server sends it; the folded D59 name otherwise. */
function chitDishName(item: KitchenChitLine, locale: Locale): string {
  if (item.baseNameAr !== undefined) {
    return locale === "en" && item.baseNameEn ? item.baseNameEn : item.baseNameAr;
  }
  return locale === "en" && item.nameEn ? item.nameEn : item.nameAr;
}

/** The variant picks, printed under the dish (only with the base name). */
function chitVariants(item: KitchenChitLine, locale: Locale): string[] {
  if (item.baseNameAr === undefined) return [];
  return (item.variantNames ?? [])
    .map((variant) =>
      locale === "en" && variant.nameEn ? variant.nameEn : variant.nameAr,
    )
    .filter((name) => Boolean(name));
}

function chitLineFacts(item: KitchenChitLine, locale: Locale) {
  return {
    dish: chitDishName(item, locale),
    variants: chitVariants(item, locale),
    addons: item.addons,
    swaps: (item.substitutions ?? []).map(
      (swap) => `${swap.removedNameAr}>${swap.addedNameAr}`,
    ),
    combo: (item.comboComponents ?? []).map(
      (component) => `${component.nameAr}×${component.quantity}`,
    ),
    note: item.note,
    // A remade plate is a correction: one per plate (T10).
    groupable: item.remakeCount === 0,
  };
}
